/**
 * BottomNav — mobile bottom navigation with a curved notch + raised teal
 * button that slides to the active tab (ported from the Claude-Design handoff).
 *
 * Icon-only bar. Insurance + Invoice are grouped under a Profile tab that opens
 * a small popover. The bar is a single SVG <path> so the notch shoulders are
 * smooth tangent fillets; it recomputes + the button slides on tab change.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

const TEAL = '#4A7A91';
const PUB  = process.env.PUBLIC_URL + '/nav-icons';

// App tab → icon. Insurance/Invoice live in the Profile popover.
const ICON = {
  'Buffing':    `${PUB}/buffing.png`,
  'Detail Queue': `${PUB}/melville-car.png`,   // renamed to bust old cached clock icon
  'Submit Job': `${PUB}/submit.png`,
  'My Jobs':    `${PUB}/myjobs.png`,
  'My Day':     `${PUB}/day.png`,        // clock glyph fits "day"
  'My Records': `${PUB}/myjobs.png`,
  'Insurance':  `${PUB}/insurance.png`,
  'Invoice':    `${PUB}/invoice.png`,
  'Statement':  `${PUB}/invoice.png`,
  'Profile':    `${PUB}/profile.png`,
};

const PROFILE_KEYS = ['Insurance', 'Invoice', 'Statement'];
// Preferred left→right order (design: Buffing, Melville, Submit, My Jobs).
const ORDER = ['Buffing', 'Submit Job', 'My Day', 'My Records', 'Detail Queue', 'My Jobs'];

// CSS filters that recolour the black-glyph PNGs.
const FILTER_GRAY  = 'brightness(0) saturate(100%) invert(68%)';
const FILTER_WHITE = 'brightness(0) invert(1)';
const FILTER_NAVY  = 'brightness(0) saturate(100%) invert(20%) sepia(18%) saturate(1200%) hue-rotate(160deg)';

// Single-path bar outline with a concave notch centred at cx (px).
function notchPath(cx, W) {
  const Y = 14, R = 39, r = 11, yc = Y + r;
  const nx = Math.sqrt(R * R - r * r), sx = nx + r;
  return [
    `M0,${Y}`,
    `H${(cx - sx).toFixed(2)}`,
    `A${r},${r} 0 0 1 ${(cx - nx).toFixed(2)},${yc}`,
    `A${R},${R} 0 0 0 ${(cx + nx).toFixed(2)},${yc}`,
    `A${r},${r} 0 0 1 ${(cx + sx).toFixed(2)},${Y}`,
    `H${W}`, `V78`, `H0`, `Z`,
  ].join(' ');
}

export default function BottomNav({ tabs, currentTab, onTabChange }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 412);
  const [showProfile, setShowProfile] = useState(false);

  // Track the real bar width so the notch geometry stays pixel-correct.
  useEffect(() => {
    const measure = () => setWidth(wrapRef.current ? wrapRef.current.clientWidth : window.innerWidth);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Split into main tabs (bar) + profile group (popover), in design order.
  const mainTabs = tabs.filter(t => !PROFILE_KEYS.includes(t))
    .sort((a, b) => {
      const ia = ORDER.indexOf(a), ib = ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  const profileTabs = tabs.filter(t => PROFILE_KEYS.includes(t));

  const items = mainTabs.map(t => ({ key: t, icon: ICON[t], isProfile: false }));
  if (profileTabs.length) items.push({ key: '__profile__', icon: ICON.Profile, isProfile: true });

  const n = items.length || 1;
  const profileIdx = items.findIndex(it => it.isProfile);

  // Which tab is highlighted (raised)?
  let baseActive = items.findIndex(it => it.key === currentTab);
  if (baseActive < 0 && PROFILE_KEYS.includes(currentTab)) baseActive = profileIdx;
  if (baseActive < 0) baseActive = 0;
  const activeIndex = showProfile && profileIdx >= 0 ? profileIdx : baseActive;

  const slot = width / n;
  const cx   = (activeIndex + 0.5) * slot;
  const activeIcon = items[activeIndex] ? items[activeIndex].icon : ICON.Profile;

  const tapTab = useCallback((it) => {
    if (it.isProfile) { setShowProfile(v => !v); return; }
    setShowProfile(false);
    onTabChange(it.key);
  }, [onTabChange]);

  const pickProfile = (key) => { setShowProfile(false); onTabChange(key); };

  const popLeft = Math.min(Math.max(cx - 90, 8), width - 188);

  return (
    <nav
      ref={wrapRef}
      className="acts-bottomnav"
      style={{ position: 'fixed', left: 0, right: 0, bottom: 0, height: 78, zIndex: 200, overflow: 'visible', pointerEvents: 'none' }}
    >
      {/* Safe-area white filler under the bar (notched phones) */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 78, height: 'env(safe-area-inset-bottom)', background: '#fff', pointerEvents: 'auto' }} />

      {/* Outside-tap catcher to close the profile popover */}
      {showProfile && (
        <div onClick={() => setShowProfile(false)}
          style={{ position: 'fixed', inset: 0, zIndex: -1, pointerEvents: 'auto', background: 'transparent' }} />
      )}

      {/* White bar with the smooth notch */}
      <svg width={width} height="78" viewBox={`0 0 ${width} 78`} preserveAspectRatio="none"
        style={{ position: 'absolute', left: 0, bottom: 0, overflow: 'visible', filter: 'drop-shadow(0 -3px 10px rgba(0,0,0,0.06))' }}>
        <path d={notchPath(cx, width)} fill="#fff" />
      </svg>

      {/* Raised active button (visual only — the tap row handles taps) */}
      <div style={{
        position: 'absolute', bottom: 34, left: cx - 30, width: 60, height: 60, borderRadius: '50%',
        background: TEAL, boxShadow: '0 8px 20px rgba(74,122,145,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        transition: 'left .38s cubic-bezier(.34,1.4,.5,1)',
      }}>
        <img src={activeIcon} alt="" style={{ width: 28, height: 28, objectFit: 'contain', filter: FILTER_WHITE }} />
      </div>

      {/* Tap row — one flex button per tab, full width */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 64, display: 'flex', pointerEvents: 'auto' }}>
        {items.map((it, i) => (
          <button key={it.key} onClick={() => tapTab(it)} aria-label={it.isProfile ? 'Profile' : it.key}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', height: '100%' }}>
            <img src={it.icon} alt="" style={{ width: 26, height: 26, objectFit: 'contain', filter: FILTER_GRAY, opacity: i === activeIndex ? 0 : 1, transition: 'opacity .2s' }} />
          </button>
        ))}
      </div>

      {/* Profile popover — Insurance + Invoice */}
      {showProfile && profileTabs.length > 0 && (
        <div style={{
          position: 'absolute', bottom: 96, left: popLeft, width: 180, background: '#fff',
          borderRadius: 14, boxShadow: '0 8px 28px rgba(0,0,0,0.16)', padding: 6, pointerEvents: 'auto', zIndex: 5,
        }}>
          {profileTabs.map(t => (
            <div key={t} onClick={() => pickProfile(t)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderRadius: 9, cursor: 'pointer' }}>
              <img src={ICON[t]} alt="" style={{ width: 22, height: 22, objectFit: 'contain', filter: FILTER_NAVY }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{t}</span>
            </div>
          ))}
          <div style={{ position: 'absolute', left: 90, bottom: -7, width: 14, height: 14, background: '#fff', transform: 'rotate(45deg)', boxShadow: '3px 3px 6px rgba(0,0,0,0.06)' }} />
        </div>
      )}
    </nav>
  );
}
